// Import only the skeleton, never `<SourceRail loading />` — the SourceRail module statically pulls
// @fs/zion-record-details into the render-blocking entry chunk; the skeleton tree-shakes clean.
import { useFeatureFlag } from '@fs/zion-flags'
import { SourceRailSkeleton } from '@fs/zion-source-list'
import { Grid, usePageWidth } from '@fs/zion-ui'
import React from 'react'
import { useAtPageContentSize } from '../PageContentWidthContext'
import FamilyMembersCardSkeleton from '../cards/family-members-card/FamilyMembersCardSkeleton'
import OtherInformationCardSkeleton from '../cards/other-information-card/OtherInformationCardSkeleton'
import OtherRelationshipsCardSkeleton from '../cards/other-relationships-card/OtherRelationshipsCardSkeleton'
import VitalsCardSkeleton from '../cards/vitals-card/VitalsCardSkeleton'
import { SOURCE_CENTRIC_SECTION_IDS, type VisibleSectionId } from './sourceCentricDetailsTabHelpers'
import { SOURCE_CENTRIC_GUTTERS } from '../constants'

export default function SourceCentricDetailsTabSkeleton(): JSX.Element {
  const pageWidth = usePageWidth()
  const atWidth = useAtPageContentSize()
  const clickableCards = useFeatureFlag('treeWebTx_personR9_clickableSourceCards').isOn
  const showSecondaryCards = pageWidth({ default: false, lg: true })
  const moveSecondaryCards = atWidth({ default: showSecondaryCards, lg: false })

  const secondaryCardsColumns = (() => {
    if (moveSecondaryCards) return 12
    if (showSecondaryCards) return 4
    return 'hide'
  })()
  const primaryCardsColumns = moveSecondaryCards || !showSecondaryCards ? 12 : 8
  const isRailSticky = showSecondaryCards && !moveSecondaryCards

  const sectionOrder: VisibleSectionId[] = [...SOURCE_CENTRIC_SECTION_IDS]

  const sectionSkeletonMap = {
    vitals: <VitalsCardSkeleton key="vitals" totalColumns={primaryCardsColumns} isSingleCardPage={false} />,
    'other-information': (
      <OtherInformationCardSkeleton
        key="other-information"
        totalColumns={primaryCardsColumns}
        isSingleCardPage={false}
      />
    ),
    'family-members': (
      <FamilyMembersCardSkeleton key="family-members" totalColumns={primaryCardsColumns} isSingleCardPage={false} />
    ),
    'other-relationships': (
      <OtherRelationshipsCardSkeleton
        key="other-relationships"
        totalColumns={primaryCardsColumns}
        isSingleCardPage={false}
      />
    ),
  } satisfies Record<VisibleSectionId, JSX.Element>

  return (
    <Grid gutters={SOURCE_CENTRIC_GUTTERS}>
      <Grid gutters={SOURCE_CENTRIC_GUTTERS} columns={primaryCardsColumns} data-testid="left-column">
        {sectionOrder.map((sectionId) => sectionSkeletonMap[sectionId]).filter(Boolean)}
      </Grid>

      <div
        columns={secondaryCardsColumns}
        data-testid="right-column"
        style={isRailSticky ? { paddingBlockStart: 'var(--spacer-nano)' } : undefined}
      >
        <SourceRailSkeleton clickableCards={clickableCards} headingLevel="h3" />
      </div>
    </Grid>
  )
}
